import { create } from "zustand"

import {
  ParentDataInterface,
  QuestionAnswerMappingInterface,
} from "../../types"
import { localStorageItems } from "../../util/storage"

export enum CoachOnboardingStep {
  // Pre-quiz intro: 2.5s "analyzing your needs" loader, then a 2-option
  // screen letting the parent take the full quiz OR skip straight to the
  // coach list with auto-filled defaults. Loader is shown once per session.
  LOADING_INTRO,
  QUIZ_OR_SKIP,
  COACH_SIGN_UP_SKIP,
  COACH_QUESTIONNAIRE_DISCLAIMER,
  POST_ANSWER_SCREEN,
  COACH_QUESTIONNAIRE,
  ADD_CHILDREN_START,
  ADD_CHILDREN,
  COACH_SIGN_UP,
  // Wheel-of-Life flow (Phase 2 of the funnel redesign). Each step is a
  // distinct screen rather than a sub-step on COACH_QUESTIONNAIRE so the
  // step machine in CoachOnboarding.tsx stays readable. The `wheelStage`
  // and `wheelDomainStep` fields below carry the within-stage progress.
  WHEEL_STAGE_1_RATINGS,
  WHEEL_INTERSTITIAL_1_TO_2,
  WHEEL_STAGE_2_GOALS,
  WHEEL_INTERSTITIAL_2_TO_3,
  WHEEL_INTERSTITIAL_3_TO_4,
  PERSONALIZED_PLAN_REVEAL,
}

export enum ChildStep {
  CHILD_INFO,
  QUESTIONNAIRE_DISCLAIMER,
  QUESTIONNAIRE,
}

// Branching-CTA entry points from jooncoach.com. The landing page now ships
// 3 cards (meltdowns / routines / all) that each deep-link here with a
// `?focus=` value. Captured once and propagated through every quiz analytic
// event so we can compare conversion by entry point in Amplitude.
//
// In Phase 2 this also drives questionnaire ordering — the matching domain
// will lead the wheel-of-life ratings. For Phase 1 we just track and pass
// it through unchanged.
export type CoachFocus = "meltdowns" | "routines" | "all"

const VALID_FOCUS: readonly CoachFocus[] = ["meltdowns", "routines", "all"]

export function isCoachFocus(value: unknown): value is CoachFocus {
  return (
    typeof value === "string" &&
    (VALID_FOCUS as readonly string[]).includes(value)
  )
}

interface CoachOnboardingStore {
  step: CoachOnboardingStep
  setStep: (step: CoachOnboardingStep) => void
  coachQuestionnaireStep: number
  setCoachQuestionnaireStep: (step: number) => void
  questionAnswerMapping: QuestionAnswerMappingInterface
  setQuestionAnswerMapping: (mapping: QuestionAnswerMappingInterface) => void
  postAnswerScreenInfo: {
    background_color_hex: string
    background_image_url: string
    button_title: string
  }
  setPostAnswerScreenInfo: (info: {
    background_color_hex: string
    background_image_url: string
    button_title: string
  }) => void

  parentData: ParentDataInterface
  setParentData: (data: ParentDataInterface) => void

  childStep: ChildStep
  setChildStep: (step: ChildStep) => void

  isSubmittingData: boolean
  setIsSubmittingData: (isSubmitting: boolean) => void

  // Branching-CTA entry-point tag from jooncoach.com.
  focus: CoachFocus | null
  setFocus: (focus: CoachFocus | null) => void

  // Wheel-of-Life state. `wheelDomainStep` is the index inside the per-stage
  // domain order (0..6). `wheelScores` is keyed by DomainKey (string) instead
  // of the enum so this file doesn't import the wheel module — keeps the
  // dependency direction store ← wheel components, not the other way.
  wheelDomainStep: number
  setWheelDomainStep: (step: number) => void
  wheelScores: Record<string, number>
  setWheelScore: (domainKey: string, score: number) => void
  wheelGoals: Record<string, string[]>
  setWheelGoals: (domainKey: string, goals: string[]) => void
  wheelSkippedDomains: Record<string, true>
  markWheelDomainSkipped: (domainKey: string) => void
  resetWheelProgress: () => void
}

// Hydrate `focus` from localStorage on store creation so that:
//   1. A user who lands on /coach-welcome with `?focus=`, then refreshes,
//      keeps their entry-point tag.
//   2. A user who completes signup and bounces back through the auth
//      round-trip preserves it for later analytics joins.
function hydrateFocus(): CoachFocus | null {
  if (typeof window === "undefined") return null
  const stored = window.localStorage.getItem(
    localStorageItems.coachOnboardingFocus
  )
  return isCoachFocus(stored) ? stored : null
}

const useCoachOnboardingStore = create<CoachOnboardingStore>((set) => ({
  // FUNNEL REVERTED (see feat/revert-to-old-coach-quiz): the strawberry-style
  // Wheel-of-Life flow added in PR #878 was reverted at Isaac's request after
  // post-launch funnel data showed heavy drop-off. We're back to the
  // pre-strawberry coach quiz — open straight into the marketing-intake
  // questionnaire. The Wheel-of-Life enum entries, components
  // (src/pages/coachOnboarding/wheelOfLife/), and store fields are kept in
  // the codebase so the work can be reintroduced behind a flag later, but
  // they are no longer reachable via the default entry point.
  step: CoachOnboardingStep.LOADING_INTRO,
  setStep: (step) => set({ step }),

  coachQuestionnaireStep: 0,
  setCoachQuestionnaireStep: (step) => set({ coachQuestionnaireStep: step }),

  questionAnswerMapping: {},
  setQuestionAnswerMapping: (mapping) =>
    set({ questionAnswerMapping: mapping }),
  postAnswerScreenInfo: {
    background_color_hex: "",
    background_image_url: "",
    button_title: "",
  },
  setPostAnswerScreenInfo: (info) => set({ postAnswerScreenInfo: info }),

  parentData: {
    name: "",
    lastName: "",
    nickname: "",
    avatar: null,
    childrenData: [],
    parentQuestionnaireMapping: {},
  },
  setParentData: (data) => set({ parentData: data }),

  childStep: ChildStep.CHILD_INFO,
  setChildStep: (step) => set({ childStep: step }),

  isSubmittingData: false,
  setIsSubmittingData: (isSubmitting) =>
    set({ isSubmittingData: isSubmitting }),

  focus: hydrateFocus(),
  setFocus: (focus) => {
    set({ focus })
    if (typeof window !== "undefined") {
      if (focus) {
        window.localStorage.setItem(
          localStorageItems.coachOnboardingFocus,
          focus
        )
      } else {
        window.localStorage.removeItem(localStorageItems.coachOnboardingFocus)
      }
    }
  },

  wheelDomainStep: 0,
  setWheelDomainStep: (step) => set({ wheelDomainStep: step }),

  wheelScores: {},
  setWheelScore: (domainKey, score) =>
    set((s) => ({
      wheelScores: { ...s.wheelScores, [domainKey]: score },
    })),

  wheelGoals: {},
  setWheelGoals: (domainKey, goals) =>
    set((s) => ({
      wheelGoals: { ...s.wheelGoals, [domainKey]: goals },
    })),

  wheelSkippedDomains: {},
  markWheelDomainSkipped: (domainKey) =>
    set((s) => ({
      wheelSkippedDomains: { ...s.wheelSkippedDomains, [domainKey]: true },
    })),

  resetWheelProgress: () =>
    set({
      wheelDomainStep: 0,
      wheelScores: {},
      wheelGoals: {},
      wheelSkippedDomains: {},
    }),
}))

export default useCoachOnboardingStore
